import { assert } from '@ember/debug';
import { tracked } from '@glimmer/tracking';
import { get } from '@ember/object';

import type Component from '@glimmer/component';

class Meta<T = unknown> {
  prevRemote?: T;
  peek!: T;
  @tracked value!: T;
}

function getOrCreateMeta<C extends Component = Component, T = unknown>(
  instance: C,
  metas: WeakMap<Component, Meta<T>>,
  initializer?: T | (Function & ((this: C) => T))
): Meta<T> {
  let meta = metas.get(instance);

  if (meta === undefined) {
    meta = new Meta();
    metas.set(instance, meta);

    meta.value = meta.peek =
      typeof initializer === 'function' ? initializer.call(instance) : initializer;
  }

  return meta;
}

/**
 * Creates a local copy of a remote value. The local copy can be updated
 * locally, but will also update if the remote value ever changes.
 *
 * @param memo The path to a "remote" property to track.
 * @param initializer This will be used if the remote value is `undefined`. If
 *   the initializer is a function, it will be called and its return value will
 *   be used as the default value.
 */
export function localCopy<C extends Component = Component, T = unknown>(
  memo: string,
  initializer?: T | (() => T)
): PropertyDecorator {
  assert(
    `@localCopy() must be given a memo path as its first argument, received \`${String(memo)}\``,
    typeof memo === 'string'
  );

  const metas = new WeakMap<Component, Meta<T>>();

  return (/* prototype, key, desc */) => {
    const memoFn = (obj: C) => get(obj, memo) as T;
    return {
      get(this: C): T {
        const meta = getOrCreateMeta<C, T>(this, metas, initializer);
        const { prevRemote } = meta;
        const incomingValue = memoFn(this);

        if (prevRemote !== incomingValue) {
          // If the incoming value is not the same as the previous incoming value,
          // update the local value to match the new incoming value, and update
          // the previous incoming value.
          meta.value = meta.prevRemote = incomingValue;
        }

        return meta.value;
      },
      set(value: T) {
        if (!metas.has(this)) {
          const meta = getOrCreateMeta(this, metas, initializer);
          meta.prevRemote = memoFn(this);
          meta.value = value;
          return;
        }

        getOrCreateMeta(this, metas, initializer).value = value;
      }
    };
  };
}
